import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brand } from "@/config/site";

const CHECKS = [
  "I have read and agree to the Dhamma Publishing House ARC Reviewer Agreement.",
  "I understand my ARC is personal and non-transferable and may contain identifying watermarks or tracking information.",
  "I understand I may not share, upload, reproduce, sell, forward, or redistribute the ARC.",
  "I intend my electronic acceptance and typed legal name to constitute my electronic signature.",
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }); }
  catch { return iso; }
}

function Shell({ children }) {
  return (
    <section className="min-h-screen bg-dh-bg px-5 pt-16 pb-24 sm:pt-20">
      <div className="mx-auto max-w-[860px]">
        <div className="flex flex-col items-center text-center">
          <img src={brand.crest} alt="Dhamma Publishing House" className="h-16 w-auto" />
          <p className="mt-5 font-body text-[11px] uppercase tracking-ultra text-dh-gold">Dhamma Publishing House</p>
          <h1 className="mt-4 font-display text-3xl leading-[1.15] text-dh-cream sm:text-5xl">
            Advance Reader Copy (ARC) Reviewer Agreement
          </h1>
          <div className="gold-rule mx-auto mt-8 w-24" />
        </div>
        {children}
        <p className="mt-16 text-center font-body text-xs text-dh-muted">
          Questions about this agreement? Contact{" "}
          <a href="mailto:Arcs@DhammaPublishingHouse.com" className="text-dh-gold underline-offset-4 hover:underline">Arcs@DhammaPublishingHouse.com</a>
        </p>
      </div>
    </section>
  );
}

function InfoRow({ k, v }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <span className="font-body text-[11px] uppercase tracking-[0.14em] text-dh-muted">{k}</span>
      <span className="col-span-1 font-body text-sm text-dh-cream/90 sm:col-span-2">{v || "—"}</span>
    </div>
  );
}

export default function ArcAgreement() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [checks, setChecks] = useState([false, false, false, false]);
  const [legalName, setLegalName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedInfo, setAcceptedInfo] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [confirmingDecline, setConfirmingDecline] = useState(false);

  useEffect(() => {
    document.title = "ARC Reviewer Agreement | Dhamma Publishing House";
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/arc/agreement/${token}`);
        setState({ loading: false, error: null, data: res.data });
      } catch (err) {
        const d = err?.response?.data?.detail;
        setState({ loading: false, error: typeof d === "string" ? d : "This agreement link is invalid, expired, or has been revoked.", data: null });
      }
    })();
  }, [token]);

  const allChecked = useMemo(() => checks.every(Boolean), [checks]);
  const canAccept = allChecked && legalName.trim().length > 0 && !submitting;

  const accept = async () => {
    if (!canAccept) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/arc/agreement/${token}/accept`, {
        legal_name: legalName.trim(),
        confirm_agreement: checks[0],
        confirm_personal: checks[1],
        confirm_no_share: checks[2],
        confirm_signature: checks[3],
      });
      setAcceptedInfo(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Acceptance could not be recorded. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      await api.post(`/arc/agreement/${token}/decline`);
      setDeclined(true);
      setConfirmingDecline(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <Shell><p className="mt-16 text-center font-body text-sm text-dh-muted">Loading your agreement…</p></Shell>;
  }

  if (state.error) {
    return (
      <Shell>
        <div data-testid="arc-agreement-error" className="mt-14 border border-dh-gold/25 bg-dh-forest/15 p-8 text-center">
          <p className="font-body text-base leading-relaxed text-dh-cream/85">{state.error}</p>
          <p className="mt-4 font-body text-sm text-dh-muted">
            If you believe this is a mistake, please contact{" "}
            <a href="mailto:Arcs@DhammaPublishingHouse.com" className="text-dh-gold underline-offset-4 hover:underline">Arcs@DhammaPublishingHouse.com</a>.
          </p>
        </div>
      </Shell>
    );
  }

  const d = state.data;

  if (declined || d.declined) {
    return (
      <Shell>
        <div data-testid="arc-agreement-declined" className="mt-14 border border-dh-cream/15 bg-dh-forest/15 p-8 text-center sm:p-10">
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Invitation Declined</p>
          <h2 className="mt-4 font-display text-3xl text-dh-cream">No problem at all.</h2>
          <p className="mx-auto mt-5 max-w-[520px] font-body text-base leading-relaxed text-dh-cream/80">
            Your ARC invitation has been withdrawn and your reserved copy will be released to another reader.
            Thank you for your time and interest in Dhamma Publishing House.
          </p>
          <p className="mt-5 font-body text-sm text-dh-muted">
            Changed your mind? Just reply to your agreement email or contact{" "}
            <a href="mailto:Arcs@DhammaPublishingHouse.com" className="text-dh-gold underline-offset-4 hover:underline">Arcs@DhammaPublishingHouse.com</a>{" "}
            and we can send a new invitation.
          </p>
        </div>
      </Shell>
    );
  }

  if (acceptedInfo || d.accepted) {
    const info = acceptedInfo || d.agreement || {};
    return (
      <Shell>
        <div data-testid="arc-agreement-accepted" className="mt-14 border border-dh-gold/25 bg-dh-forest/15 p-8 sm:p-10">
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Agreement Accepted</p>
          <h2 className="mt-4 font-display text-3xl text-dh-cream">Thank you, {info.legal_name || "Reviewer"}.</h2>
          <p className="mt-5 font-body text-base leading-relaxed text-dh-cream/80">
            Your acceptance of the Dhamma Publishing House ARC Reviewer Agreement has been recorded.
            Dhamma Publishing House will prepare your ARC delivery separately — no action is needed from you right now.
          </p>
          <div className="mt-8 space-y-3 border-t border-dh-cream/10 pt-6">
            <InfoRow k="Agreement version" v={info.agreement_version} />
            <InfoRow k="Copy ID" v={info.assigned_copy_id} />
            <InfoRow k="Accepted" v={fmt(info.accepted_at)} />
          </div>
        </div>
        <p className="mt-10 text-center font-body text-sm text-dh-muted">
          <Link to="/" className="text-dh-gold underline-offset-4 hover:underline">Return to Dhamma Publishing House</Link>
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="arc-agreement-page" className="mt-14">
        <div className="border border-dh-gold/25 bg-dh-forest/15 p-7 sm:p-9">
          <p className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Reviewer & Copy Details</p>
          <div className="mt-5 space-y-3">
            <InfoRow k="Reviewer" v={d.reviewer_name} />
            <InfoRow k="Reviewer email" v={d.email} />
            <InfoRow k="ARC title" v={d.title} />
            <InfoRow k="Assigned copy ID" v={d.assigned_copy_id} />
            <InfoRow k="Issue date" v={fmt(d.issue_date)} />
            <InfoRow k="Review window" v={d.review_window} />
            <InfoRow k="Embargo date" v={d.embargo_date} />
            <InfoRow k="Agreement version" v={d.agreement_version} />
          </div>
        </div>

        <div className="mt-12 space-y-8">
          <p className="font-body text-base leading-relaxed text-dh-cream/85">{d.intro}</p>
          {d.sections.map((s) => (
            <div key={s.heading}>
              <h2 className="font-display text-xl text-dh-gold">{s.heading}</h2>
              {s.body.split("\n").map((p, i) => (
                <p key={i} className="mt-3 font-body text-sm leading-relaxed text-dh-cream/75">{p}</p>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-14 border border-dh-gold/30 bg-dh-bg2/60 p-7 sm:p-9">
          <p className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Acceptance</p>
          <div className="mt-6 space-y-3">
            {CHECKS.map((c, i) => (
              <label key={c} data-testid={`arc-agree-check-${i + 1}`} className="flex cursor-pointer items-start gap-3 border border-dh-cream/10 bg-dh-bg/60 p-4 transition-colors hover:border-dh-gold/40">
                <input type="checkbox" checked={checks[i]} onChange={() => setChecks((s) => s.map((v, j) => (j === i ? !v : v)))} className="mt-1 h-4 w-4 accent-dh-gold" />
                <span className="font-body text-sm leading-relaxed text-dh-cream/85">{c}</span>
              </label>
            ))}
          </div>
          <div className="mt-7">
            <label htmlFor="legal_name" className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted">Legal Name *</label>
            <input id="legal_name" data-testid="arc-legal-name" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Type your full legal name" className="w-full border-b border-dh-cream/20 bg-transparent px-1 py-3 font-body text-base text-dh-cream placeholder:text-dh-muted/60 outline-none transition-colors duration-300 focus:border-dh-gold" />
          </div>
          <button data-testid="arc-accept-agreement" onClick={accept} disabled={!canAccept} className="mt-9 inline-flex items-center justify-center gap-3 border border-dh-gold/70 bg-transparent px-10 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold transition-[background-color,color] duration-500 hover:bg-dh-gold hover:text-dh-bg2 disabled:cursor-not-allowed disabled:opacity-40">
            {submitting ? "Recording…" : "Accept Agreement"}
          </button>
          <p className="mt-4 font-body text-xs leading-relaxed text-dh-muted">
            Your acceptance, agreement version, and acceptance time will be recorded as your electronic signature.
          </p>
        </div>

        <div className="mt-10 border-t border-dh-cream/10 pt-8 text-center">
          {!confirmingDecline ? (
            <button data-testid="arc-decline-agreement" onClick={() => setConfirmingDecline(true)} className="font-body text-xs uppercase tracking-[0.18em] text-dh-muted underline-offset-4 transition-colors hover:text-dh-cream hover:underline">
              I do not wish to participate
            </button>
          ) : (
            <div data-testid="arc-decline-confirm">
              <p className="font-body text-sm text-dh-cream/80">
                Decline this ARC invitation? Your reserved copy will be released. You can ask for a new invitation later.
              </p>
              <div className="mt-4 flex items-center justify-center gap-4">
                <button data-testid="arc-decline-yes" onClick={decline} disabled={submitting} className="border border-dh-cream/30 px-6 py-2.5 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/80 transition-colors hover:border-dh-cream/70 hover:text-dh-cream disabled:opacity-40">
                  Yes, decline
                </button>
                <button data-testid="arc-decline-cancel" onClick={() => setConfirmingDecline(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold underline-offset-4 hover:underline">
                  Keep my invitation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
